/**
 * Validation functions for Cloud Propagation rules
 * Centralized validation utilities for routing tables, form data, and other Cloud Propagation components
 */

import { validateRtb, Cloud } from './bgpValidation';

/**
 * Validates a single routing table ID
 * @param input - The routing table ID to validate
 * @returns Validation result with valid boolean and cloud type
 */
export function validateRoutingTable(input: string): { valid: boolean; cloud: Cloud } {
  return validateRtb(input);
}

/**
 * Validates an array of routing table IDs and filters out invalid ones
 * @param routingTables - Array of routing table IDs to validate
 * @param routingTableOptions - Optional array of valid dropdown options (these are always considered valid)
 * @returns Array of valid routing table IDs
 */
export function validateAndFilterRoutingTables(
  routingTables: string[],
  routingTableOptions?: Array<{ value: string; label: string }>
): string[] {
  if (!Array.isArray(routingTables)) return [];
  
  return routingTables.filter(rt => {
    if (!rt || !rt.trim()) return false;
    const trimmed = rt.trim();
    
    // Check if it's from dropdown options (always valid)
    if (routingTableOptions && routingTableOptions.some(opt => opt.value === trimmed)) {
      return true;
    }
    
    // Validate custom values
    return validateRtb(trimmed).valid;
  });
}

/**
 * Validates routing table input and returns error message if invalid
 * @param input - The routing table ID to validate
 * @param routingTableOptions - Optional array of valid dropdown options
 * @returns Error message string if invalid, empty string if valid
 */
export function getRoutingTableValidationError(
  input: string,
  routingTableOptions?: Array<{ value: string; label: string }>
): string {
  if (!input || !input.trim()) {
    return ""; // Empty is valid (will be validated by required check)
  }
  
  const trimmed = input.trim();
  
  // Check if it's from dropdown options (always valid)
  if (routingTableOptions && routingTableOptions.some(opt => opt.value === trimmed)) {
    return "";
  }
  
  // Validate custom values
  const validation = validateRtb(trimmed);
  if (!validation.valid) {
    return "Invalid routing table format. Please enter a valid AWS, OCI, Azure, or GCP routing table ID.";
  }
  
  return "";
}

/**
 * Checks if a routing table is valid (either from dropdown or passes validation)
 * @param input - The routing table ID to check
 * @param routingTableOptions - Optional array of valid dropdown options
 * @returns True if valid, false otherwise
 */
export function isRoutingTableValid(
  input: string,
  routingTableOptions?: Array<{ value: string; label: string }>
): boolean {
  if (!input || !input.trim()) return false;
  
  const trimmed = input.trim();
  
  // Check if it's from dropdown options (always valid)
  if (routingTableOptions && routingTableOptions.some(opt => opt.value === trimmed)) {
    return true;
  }
  
  // Validate custom values
  return validateRtb(trimmed).valid;
}

// Re-export validateRtb for convenience
export { validateRtb, type Cloud } from './bgpValidation';
